import React, { useState, useEffect, useCallback } from 'react';
import { animated, useSpring, useTransition } from 'react-spring'; // Aggiunto useTransition
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import ProgressBar from './ProgressBar.js'; // Importa il componente ProgressBar
import './LeftSidebar.css'; // Importa il file CSS per LeftSidebar

// Immagine placeholder per il profilo. Sostituiscila con la tua URL reale.
// const profilePicture = 'https://placehold.co/120x120/00FFFF/000000?text=PZ'; // Esempio: azzurro con testo nero
import profilePicture from './img/square-bw-01-L.jpg';
/**
 * LeftSidebar Component.
 * Visualizza una sidebar fissa a sinistra, collassabile, con informazioni di profilo e skill.
 *
 * @param {object} props - Component props.
 * @param {boolean} props.isCollapsed - Controlla se la sidebar è collassata o espansa.
 * @param {function} props.onToggleCollapse - Funzione per cambiare lo stato di collasso.
 */
function LeftSidebar({ isCollapsed, onToggleCollapse, onTopicClick, footerHeight }) {
    
    const [isProfileInfoVisible, setIsProfileInfoVisible] = useState(false);
    
    // Gestore per il click sull'icona delle info (mostra/nasconde il testo)
    const handleInfoClick = useCallback(() => {
        setIsProfileInfoVisible(prev => !prev);
    }, []);
    
    // Reimposta la visibilità delle info quando la sidebar si contrae
    useEffect(() => {
        if (isCollapsed) {
            setIsProfileInfoVisible(false);
        }
    }, [isCollapsed]);
    
    // Definisci le skill con le percentuali. Puoi espandere questa lista.
    const skills = [
        { name: 'C++', percentage: 90 },
        { name: 'Python', percentage: 55 },
        { name: 'Javascript', percentage: 80 },
        { name: 'React JS', percentage: 70 },
        { name: 'Node JS', percentage: 20 },
        { name: 'PHP', percentage: 55 },
        
        { name: 'DSP', percentage: 85 },
        
        
        
        { name: 'Wordpress', percentage: 85 },        
        
        { name: 'CSS', percentage: 90 },        
        { name: 'SQL', percentage: 50 },
        
        { name: 'Sound Design', percentage: 95 },
        { name: 'Graphic/Motion Design', percentage: 90 },
        
        
        // Aggiungi altre skill qui
    ];

    // Animazione per la larghezza della sidebar
    const sidebarAnimation = useSpring({
        width: isCollapsed ? '60px' : '250px', // Larghezza collassata vs. espansa
        config: { mass: 1, tension: 400, friction: 20 }
    });

    // Animazione per l'opacità del contenuto che appare/scompare con l'espansione
    const expandedContentOpacityAnimation = useSpring({
        opacity: isCollapsed ? 0 : 1,
        // Questa durata dovrebbe essere coordinata con la durata di 'leave' del collapsedTextTransition
        config: { duration: isCollapsed ? 100 : 300 }, // Rendiamo uniforme la durata per coerenza
    });

    // useTransition per il testo "About Me" collassato
    // Gestirà l'entrata e l'uscita del componente per l'animazione di fade
    const collapsedTextTransition = useTransition(isCollapsed ? 'aboutMe' : null, {
        from: { opacity: 0, transform: 'translateX(20px)' }, // Inizia invisibile e leggermente a destra
        enter: { opacity: 1, transform: 'translateX(0px)' }, // Appare e si sposta a sinistra
        // Modificato: Effetto di scomparsa (leave) con translateX e senza scale
        leave: { opacity: 0, transform: 'translateX(-250px)' }, // Si sposta leggermente a sinistra
        config: { duration: 300 } // Durata della transizione di fade (controlla la velocità di scomparsa/comparsa)
    });


    return (
        <animated.div
            className={`left-sidebar ${isCollapsed ? 'is-collapsed' : ''}`} 
            style={{
                ...sidebarAnimation,
                height: `calc(100vh - ${footerHeight}px)`,
                cursor: isCollapsed ? 'pointer' : 'default' // Cambia cursore quando collassata
            }}
            // Rende la sidebar collassata cliccabile (ma non quando il bottone viene cliccato)
            onClick={isCollapsed ? onToggleCollapse : undefined}
        >
            <button
                onClick={(e) => {
                    e.stopPropagation(); // Ferma la propagazione dell'evento per evitare il click sul parent
                    onToggleCollapse(); // Chiama la funzione di toggle
                }}
                className="collapse-toggle-button"
            >
                {isCollapsed ? '►' : '◄'} {/* Icona per collassare/espandere */}
            </button>

            {/* Testo collassato, animato da useTransition */}
            {collapsedTextTransition((style, item) =>
                item && ( // Renderizza solo se 'item' esiste (ovvero, isCollapsed è true)
                    <animated.div className="collapsed-text-container" style={style}>
                        {/* Il testo ruotato rimane invariato, l'animazione è sul suo contenitore */}
                        <p className="collapsed-text">About Me</p>
                    </animated.div>
                )
            )}

            {/* Contenuto che appare quando espanso e svanisce quando collassato */}
            <animated.div
                className="sidebar-content"
                style={{
                    ...expandedContentOpacityAnimation, // Applica le proprietà di opacità e transizione
                    // Mappa l'opacità a display: 'none' quando è completamente trasparente
                    display: expandedContentOpacityAnimation.opacity.to(o => o === 0 ? 'none' : 'block')
                }}
            >
                {/* Sezione Profilo */}
                <div className="profile-section">
                    <img src={profilePicture} alt="Profile" className="profile-picture" />
                    <h4 className="profile-name">Paolo Zappalà</h4>
                    <p className="profile-title">Sound Designer, Software Developer</p>
                </div>

                {/* Sezione Skills */}
                <div className="skills-section">
                    <h4 className="skills-title">Skills</h4>
                    <div className="progress-bars-container">
                        {skills.map((skill) => (
                            <ProgressBar
                                key={skill.name}
                                skill={skill.name} // Always pass skill name, ProgressBar component will decide to render it.
                                percentage={skill.percentage}
                            />
                        ))}
                    </div>
                </div>
            </animated.div>
            
            
            {!isCollapsed && (
                <animated.div className="sidebar-footer"
                style={{
                    ...expandedContentOpacityAnimation, // Applica le proprietà di opacità e transizione
                    // Mappa l'opacità a display: 'none' quando è completamente trasparente
                    display: expandedContentOpacityAnimation.opacity.to(o => o === 0 ? 'none' : 'block')
                }}>
                    <button className="contact-popup-link" onClick={() => {onTopicClick('contact'); onToggleCollapse()} }>
                        <h3>CONTACT ME</h3>
                    </button>
                </animated.div>
            )}
            
            
            
            
        </animated.div>
    );
}

export default LeftSidebar;
